import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Package, Folder, ShoppingBag, LogOut, Briefcase, FileText, Book } from 'lucide-react';
import { supabase } from '../supabaseClient';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  const checkAuth = React.useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    const init = async () => {
      await checkAuth();
      fetchData();
    };
    init();
  }, [checkAuth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, ordersRes, portfolioRes, blogRes, docsRes] = await Promise.all([
        supabase.from('products').select('*, categories(name)').order('created_at', { ascending: false }),
        supabase.from('categories').select('*').order('name'),
        supabase.from('orders').select('*, products(name)').order('created_at', { ascending: false }),
        supabase.from('portfolio_items').select('*').order('created_at', { ascending: false }),
        supabase.from('blog_posts').select('*').order('created_at', { ascending: false }),
        supabase.from('documentation').select('*').order('created_at', { ascending: false })
      ]);

      setProducts(productsRes.data || []);
      setCategories(categoriesRes.data || []);
      setOrders(ordersRes.data || []);
      setPortfolioItems(portfolioRes.data || []);
      setBlogPosts(blogRes.data || []);
      setDocs(docsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setEditingItem(null);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const tableMap = {
        'product': 'products',
        'category': 'categories',
        'portfolio': 'portfolio_items',
        'blog': 'blog_posts',
        'doc': 'documentation'
      };
      const table = tableMap[type];
      const { error } = await supabase.from(table).delete().eq('id', id);
      
      if (error) throw error;
      
      fetchData();
      alert('Deleted successfully!');
    } catch (error) {
      alert('Error deleting: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-poppins mb-2">
              Admin <span className="gradient-text">Dashboard</span>
            </h1>
            <p className="text-gray-400">Manage your products, categories, and orders</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 glass-card rounded-lg hover:bg-gray-800/50 transition"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="glass-card rounded-lg mb-8 overflow-x-auto">
          <div className="flex border-b border-gray-800 min-w-max">
            <button
              onClick={() => setActiveTab('products')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition ${
                activeTab === 'products'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Package size={20} />
              <span>Products ({products.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition ${
                activeTab === 'categories'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Folder size={20} />
              <span>Categories ({categories.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('portfolio')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition ${
                activeTab === 'portfolio'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Briefcase size={20} />
              <span>Portfolio ({portfolioItems.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('blog')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition ${
                activeTab === 'blog'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <FileText size={20} />
              <span>Blog ({blogPosts.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('docs')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition ${
                activeTab === 'docs'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Book size={20} />
              <span>Docs ({docs.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition ${
                activeTab === 'orders'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <ShoppingBag size={20} />
              <span>Orders ({orders.length})</span>
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <div>
            {activeTab === 'products' && (
              <ProductsTab
                products={products}
                categories={categories}
                onAdd={() => openModal('product')}
                onEdit={(product) => openModal('product', product)}
                onDelete={(id) => handleDelete('product', id)}
              />
            )}
            {activeTab === 'categories' && (
              <CategoriesTab
                categories={categories}
                onAdd={() => openModal('category')}
                onEdit={(category) => openModal('category', category)}
                onDelete={(id) => handleDelete('category', id)}
              />
            )}
            {activeTab === 'portfolio' && (
              <PortfolioTab
                items={portfolioItems}
                onAdd={() => openModal('portfolio')}
                onEdit={(item) => openModal('portfolio', item)}
                onDelete={(id) => handleDelete('portfolio', id)}
              />
            )}
            {activeTab === 'blog' && (
              <BlogTab
                posts={blogPosts}
                onAdd={() => openModal('blog')}
                onEdit={(post) => openModal('blog', post)}
                onDelete={(id) => handleDelete('blog', id)}
              />
            )}
            {activeTab === 'docs' && (
              <DocsTab
                docs={docs}
                onAdd={() => openModal('doc')}
                onEdit={(doc) => openModal('doc', doc)}
                onDelete={(id) => handleDelete('doc', id)}
              />
            )}
            {activeTab === 'orders' && <OrdersTab orders={orders} />}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <Modal
          type={modalType}
          item={editingItem}
          categories={categories}
          onClose={closeModal}
          onSave={() => {
            closeModal();
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// Products Tab Component
const ProductsTab = ({ products, categories, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-2xl font-bold">Products</h2>
      <button onClick={onAdd} className="gradient-button flex items-center space-x-2">
        <Plus size={18} />
        <span>Add Product</span>
      </button>
    </div>
    {products.length === 0 ? (
      <div className="glass-card p-12 text-center rounded-lg">
        <Package size={48} className="mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400">No products yet. Add your first product!</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-4">
        {products.map((product) => (
          <div key={product.id} className="glass-card p-6 rounded-lg flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-bold">{product.name}</h3>
                {product.is_featured && (
                  <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">Featured</span>
                )}
              </div>
              <p className="text-gray-400 text-sm mb-2">{product.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span>₹{product.price}</span>
                <span>•</span>
                <span>{product.categories?.name || 'No category'}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onEdit(product)}
                className="p-2 hover:bg-gray-800/50 rounded transition"
              >
                <Edit2 size={18} className="text-cyan-400" />
              </button>
              <button
                onClick={() => onDelete(product.id)}
                className="p-2 hover:bg-gray-800/50 rounded transition"
              >
                <Trash2 size={18} className="text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Categories Tab Component
const CategoriesTab = ({ categories, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-2xl font-bold">Categories</h2>
      <button onClick={onAdd} className="gradient-button flex items-center space-x-2">
        <Plus size={18} />
        <span>Add Category</span>
      </button>
    </div>
    {categories.length === 0 ? (
      <div className="glass-card p-12 text-center rounded-lg">
        <Folder size={48} className="mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400">No categories yet. Add your first category!</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((category) => (
          <div key={category.id} className="glass-card p-6 rounded-lg">
            <div className="flex items-start justify-between mb-4">
              <div className="text-4xl">{category.icon}</div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onEdit(category)}
                  className="p-2 hover:bg-gray-800/50 rounded transition"
                >
                  <Edit2 size={18} className="text-cyan-400" />
                </button>
                <button
                  onClick={() => onDelete(category.id)}
                  className="p-2 hover:bg-gray-800/50 rounded transition"
                >
                  <Trash2 size={18} className="text-red-400" />
                </button>
              </div>
            </div>
            <h3 className="text-lg font-bold mb-2">{category.name}</h3>
            <p className="text-gray-400 text-sm">{category.description}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Orders Tab Component
const OrdersTab = ({ orders }) => (
  <div>
    <h2 className="text-2xl font-bold mb-6">Orders</h2>
    {orders.length === 0 ? (
      <div className="glass-card p-12 text-center rounded-lg">
        <ShoppingBag size={48} className="mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400">No orders yet</p>
      </div>
    ) : (
      <div className="space-y-4">
        {orders.map((order) => (
          <div key={order.id} className="glass-card p-6 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">{order.products?.name || 'Unknown Product'}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                order.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {order.status}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-400">
              <div>
                <span className="text-gray-500">Customer:</span> {order.customer_name}
              </div>
              <div>
                <span className="text-gray-500">Email:</span> {order.customer_email}
              </div>
              <div>
                <span className="text-gray-500">Amount:</span> ₹{order.amount}
              </div>
              <div>
                <span className="text-gray-500">Payment ID:</span> {order.razorpay_payment_id || 'N/A'}
              </div>
            </div>
            {!order.github_invite_sent && (
              <div className="mt-4">
                <button className="gradient-button text-sm px-4 py-2">
                  Send GitHub Invite
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

// Modal Component
const Modal = ({ type, item, categories, onClose, onSave }) => {
  const getInitialData = () => {
    if (item) return item;
    
    switch(type) {
      case 'portfolio':
        return { title: '', description: '', category: '', tech_stack: [], image_url: '', github_url: '', live_url: '' };
      case 'blog':
        return { title: '', slug: '', excerpt: '', content: '', category: '', author: 'Script Saint Team', image_url: '', read_time: 5, published: false };
      case 'doc':
        return { title: '', description: '', type: 'guide', url: '', download_url: '', read_time: 5, is_new: false };
      case 'category':
        return { name: '', description: '', icon: '📦' };
      default:
        return { name: '', description: '', price: '', category_id: '', tech_stack: [], features: [], image_url: '', demo_url: '', github_repo: '', is_featured: false, rating: 5.0 };
    }
  };

  const [formData, setFormData] = useState(getInitialData());
  const [techInput, setTechInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const tableMap = {
        'product': 'products',
        'category': 'categories',
        'portfolio': 'portfolio_items',
        'blog': 'blog_posts',
        'doc': 'documentation'
      };
      const table = tableMap[type];
      
      let data = { ...formData };
      if (type === 'product') {
        data.price = parseFloat(data.price);
        data.rating = parseFloat(data.rating);
      }
      if (type === 'blog' || type === 'doc') {
        data.read_time = parseInt(data.read_time);
      }

      if (item) {
        const { error } = await supabase.from(table).update(data).eq('id', item.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from(table).insert([data]);
        if (error) throw error;
      }

      alert('Saved successfully!');
      onSave();
    } catch (error) {
      alert('Error saving: ' + error.message);
    }
  };

  const addTech = () => {
    if (techInput.trim()) {
      setFormData({ ...formData, tech_stack: [...(formData.tech_stack || []), techInput.trim()] });
      setTechInput('');
    }
  };

  const removeTech = (index) => {
    const newTech = formData.tech_stack.filter((_, i) => i !== index);
    setFormData({ ...formData, tech_stack: newTech });
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({ ...formData, features: [...(formData.features || []), featureInput.trim()] });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const getModalTitle = () => {
    const titles = {
      'product': 'Product',
      'category': 'Category',
      'portfolio': 'Portfolio Item',
      'blog': 'Blog Post',
      'doc': 'Documentation'
    };
    return `${item ? 'Edit' : 'Add'} ${titles[type]}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="glass-card p-8 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">{getModalTitle()}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {type === 'portfolio' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  rows={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  placeholder="E-Commerce, SaaS, Trading, etc."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tech Stack (comma-separated)</label>
                <input
                  type="text"
                  value={Array.isArray(formData.tech_stack) ? formData.tech_stack.join(', ') : ''}
                  onChange={(e) => setFormData({ ...formData, tech_stack: e.target.value.split(',').map(s => s.trim()) })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  placeholder="React, Node.js, MongoDB"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">GitHub URL</label>
                <input
                  type="url"
                  value={formData.github_url}
                  onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Live URL</label>
                <input
                  type="url"
                  value={formData.live_url}
                  onChange={(e) => setFormData({ ...formData, live_url: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
            </>
          ) : type === 'blog' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Slug (URL-friendly)</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  placeholder="my-blog-post"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  rows={2}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  rows={6}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                    placeholder="Tutorial, Guide, News"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Author</label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Image URL</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Read Time (min)</label>
                  <input
                    type="number"
                    value={formData.read_time}
                    onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="published" className="text-sm text-gray-300">Published</label>
              </div>
            </>
          ) : type === 'doc' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  rows={3}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                    required
                  >
                    <option value="guide">Guide</option>
                    <option value="tutorial">Tutorial</option>
                    <option value="video">Video</option>
                    <option value="api">API</option>
                    <option value="resource">Resource</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Read Time (min)</label>
                  <input
                    type="number"
                    value={formData.read_time}
                    onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">URL</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Download URL</label>
                <input
                  type="url"
                  value={formData.download_url}
                  onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_new"
                  checked={formData.is_new}
                  onChange={(e) => setFormData({ ...formData, is_new: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_new" className="text-sm text-gray-300">Mark as New</label>
              </div>
            </>
          ) : type === 'category' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Icon (Emoji)</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  placeholder="📦"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  rows={3}
                  required
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  rows={3}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Price (₹)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                    required
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Demo URL</label>
                <input
                  type="url"
                  value={formData.demo_url}
                  onChange={(e) => setFormData({ ...formData, demo_url: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">GitHub Repo URL</label>
                <input
                  type="url"
                  value={formData.github_repo}
                  onChange={(e) => setFormData({ ...formData, github_repo: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tech Stack</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={techInput}
                    onChange={(e) => setTechInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTech())}
                    className="flex-1 px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                    placeholder="e.g., React"
                  />
                  <button type="button" onClick={addTech} className="px-4 py-2 bg-cyan-500 rounded-lg">Add</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(formData.tech_stack || []).map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-800 rounded-full text-sm flex items-center gap-2">
                      {tech}
                      <button type="button" onClick={() => removeTech(index)} className="text-red-400">×</button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Features</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    className="flex-1 px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                    placeholder="e.g., Full authentication system"
                  />
                  <button type="button" onClick={addFeature} className="px-4 py-2 bg-cyan-500 rounded-lg">Add</button>
                </div>
                <div className="space-y-2">
                  {(formData.features || []).map((feature, index) => (
                    <div key={index} className="px-3 py-2 bg-gray-800 rounded text-sm flex items-center justify-between">
                      {feature}
                      <button type="button" onClick={() => removeFeature(index)} className="text-red-400">×</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="featured" className="text-sm text-gray-300">Featured Product</label>
              </div>
            </>
          )}
          <div className="flex gap-4 mt-6">
            <button type="submit" className="flex-1 gradient-button py-3">
              {item ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={onClose} className="flex-1 glass-card py-3 rounded-lg">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Portfolio Tab Component
const PortfolioTab = ({ items, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-2xl font-bold">Portfolio Items</h2>
      <button onClick={onAdd} className="gradient-button flex items-center space-x-2">
        <Plus size={18} />
        <span>Add Portfolio Item</span>
      </button>
    </div>
    {items.length === 0 ? (
      <div className="glass-card p-12 text-center rounded-lg">
        <Briefcase size={48} className="mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400">No portfolio items yet. Add your first one!</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-4">
        {items.map((item) => (
          <div key={item.id} className="glass-card p-6 rounded-lg flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">{item.title}</h3>
              <p className="text-gray-400 text-sm mb-2">{item.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded">{item.category}</span>
                {item.tech_stack && <span>Tech: {item.tech_stack.join(', ')}</span>}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => onEdit(item)} className="p-2 hover:bg-gray-800/50 rounded transition">
                <Edit2 size={18} className="text-cyan-400" />
              </button>
              <button onClick={() => onDelete(item.id)} className="p-2 hover:bg-gray-800/50 rounded transition">
                <Trash2 size={18} className="text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Blog Tab Component
const BlogTab = ({ posts, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-2xl font-bold">Blog Posts</h2>
      <button onClick={onAdd} className="gradient-button flex items-center space-x-2">
        <Plus size={18} />
        <span>Add Blog Post</span>
      </button>
    </div>
    {posts.length === 0 ? (
      <div className="glass-card p-12 text-center rounded-lg">
        <FileText size={48} className="mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400">No blog posts yet. Add your first one!</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-4">
        {posts.map((post) => (
          <div key={post.id} className="glass-card p-6 rounded-lg flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-bold">{post.title}</h3>
                {post.published ? (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Published</span>
                ) : (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Draft</span>
                )}
              </div>
              <p className="text-gray-400 text-sm mb-2">{post.excerpt}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded">{post.category}</span>
                <span>By {post.author}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => onEdit(post)} className="p-2 hover:bg-gray-800/50 rounded transition">
                <Edit2 size={18} className="text-cyan-400" />
              </button>
              <button onClick={() => onDelete(post.id)} className="p-2 hover:bg-gray-800/50 rounded transition">
                <Trash2 size={18} className="text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Documentation Tab Component
const DocsTab = ({ docs, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-2xl font-bold">Documentation</h2>
      <button onClick={onAdd} className="gradient-button flex items-center space-x-2">
        <Plus size={18} />
        <span>Add Documentation</span>
      </button>
    </div>
    {docs.length === 0 ? (
      <div className="glass-card p-12 text-center rounded-lg">
        <Book size={48} className="mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400">No documentation yet. Add your first one!</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-4">
        {docs.map((doc) => (
          <div key={doc.id} className="glass-card p-6 rounded-lg flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-bold">{doc.title}</h3>
                {doc.is_new && (
                  <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">NEW</span>
                )}
              </div>
              <p className="text-gray-400 text-sm mb-2">{doc.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded capitalize">{doc.type}</span>
                <span>{doc.read_time} min read</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => onEdit(doc)} className="p-2 hover:bg-gray-800/50 rounded transition">
                <Edit2 size={18} className="text-cyan-400" />
              </button>
              <button onClick={() => onDelete(doc.id)} className="p-2 hover:bg-gray-800/50 rounded transition">
                <Trash2 size={18} className="text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default AdminDashboard;
